import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Space, Typography, notification, Radio, Popconfirm } from 'antd';
import { CheckCircleFilled, SaveOutlined, CloseCircleFilled, CheckCircleTwoTone, ClockCircleFilled, ExclamationCircleFilled, CloseCircleTwoTone, CheckCircleOutlined, CheckOutlined, UndoOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { 
  getBusinessOrganisationDetails, 
  updateBusinessOrganisation
} from '../../actions/businessOrganisationActions';
import { BUSINESS_ORGANISATION_UPDATE_RESET } from '../../constants/businessOrganisationConstants';
import DSAComponent from './DSAComponent';
import DPAComponent from './DPAComponent';
import TOCComponent from './TOCComponent';

const { Text, Title } = Typography;

const AgreementUpdateModal = ({ 
  visible, 
  onCancel, 
  organisationId,
  selectedAgreement = null // 'dsa', 'dpa', or 'toc'
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  
  // Redux state
  const businessOrganisationDetails = useSelector(state => state.businessOrganisationDetails);
  const { loading: loadingDetails, businessOrganisation: organisation } = businessOrganisationDetails;
  
  const businessOrganisationUpdate = useSelector(state => state.businessOrganisationUpdate);
  const { loading: loadingUpdate, success: successUpdate, error: errorUpdate } = businessOrganisationUpdate;
  
  const [currentSelectedAgreement, setCurrentSelectedAgreement] = useState(selectedAgreement);

  // Get icon based on current agreement status
  const getStatusIcon = () => {
    if (!currentSelectedAgreement || !organisation) {
      return <CheckCircleFilled />;
    }

    const currentStatus = currentSelectedAgreement === 'dsa' ? organisation.dsaStatus :
                         currentSelectedAgreement === 'dpa' ? organisation.dpaStatus :
                         currentSelectedAgreement === 'toc' ? organisation.tocStatus : 'NotSigned';

    switch (currentStatus) {
      case 'Signed':
        return <CheckCircleTwoTone twoToneColor="#52c41a" />;
      case 'InProgress':
        return <ClockCircleFilled style={{ color: '#1890ff' }} />;
      case 'Pending':
        return <ExclamationCircleFilled style={{ color: '#faad14' }} />;
      case 'NotSigned':
      default:
        return <CloseCircleFilled style={{ color: '#ff4d4f' }} />;
    }
  };

  // Load organisation data when modal opens
  useEffect(() => {
    if (visible && organisationId) {
      dispatch(getBusinessOrganisationDetails(organisationId));
    }
  }, [visible, organisationId, dispatch]);

  // Set form values when organisation data is loaded
  useEffect(() => {
    if (organisation && visible && selectedAgreement) {
      setCurrentSelectedAgreement(selectedAgreement);
      
      // Get current values from organisation - NO AUTO-SELECTION OR DEFAULTS
      const currentStatus = selectedAgreement === 'dsa' ? organisation.dsaStatus :
                           selectedAgreement === 'dpa' ? organisation.dpaStatus :
                           selectedAgreement === 'toc' ? organisation.tocStatus : 'NotSigned';
                           
      const currentSignedDate = selectedAgreement === 'dsa' ? organisation.dsaSignedDate :
                               selectedAgreement === 'dpa' ? organisation.dpaSignedDate :
                               selectedAgreement === 'toc' ? organisation.tocSignedDate : null;
                               
      const currentSignedBy = selectedAgreement === 'dsa' ? organisation.dsaSignedBy :
                             selectedAgreement === 'dpa' ? organisation.dpaSignedBy :
                             selectedAgreement === 'toc' ? organisation.tocSignedBy : '';
                             
      const currentJsonStr = selectedAgreement === 'dsa' ? organisation.dsaDocumentJson :
                            selectedAgreement === 'dpa' ? organisation.dpaDocumentJson :
                            selectedAgreement === 'toc' ? organisation.tocDocumentJson : null;

      // Set form values exactly as they are in the database
      const formData = {};
      formData[`${selectedAgreement}Status`] = currentStatus;
      formData[`${selectedAgreement}SignedDate`] = currentSignedDate ? moment(currentSignedDate) : null;
      formData[`${selectedAgreement}SignedBy`] = currentSignedBy;
      
      // Parse existing JSON or use empty object
      let jsonData = {};
      try {
        jsonData = currentJsonStr ? JSON.parse(currentJsonStr) : {};
      } catch (e) {
        jsonData = {};
      }
      formData[selectedAgreement] = jsonData;
      
      form.setFieldsValue(formData);
    }
  }, [organisation, visible, selectedAgreement, form]);

  // Handle agreement type selection for manual mode (when selectedAgreement is null)
  useEffect(() => {
    if (currentSelectedAgreement && organisation && !selectedAgreement) {
      // Load data for the newly selected agreement - NO AUTO-SELECTION OR DEFAULTS
      const currentStatus = currentSelectedAgreement === 'dsa' ? organisation.dsaStatus :
                           currentSelectedAgreement === 'dpa' ? organisation.dpaStatus :
                           currentSelectedAgreement === 'toc' ? organisation.tocStatus : 'NotSigned';
                           
      const currentSignedDate = currentSelectedAgreement === 'dsa' ? organisation.dsaSignedDate :
                               currentSelectedAgreement === 'dpa' ? organisation.dpaSignedDate :
                               currentSelectedAgreement === 'toc' ? organisation.tocSignedDate : null;
                               
      const currentSignedBy = currentSelectedAgreement === 'dsa' ? organisation.dsaSignedBy :
                             currentSelectedAgreement === 'dpa' ? organisation.dpaSignedBy :
                             currentSelectedAgreement === 'toc' ? organisation.tocSignedBy : '';
                             
      const currentJsonStr = currentSelectedAgreement === 'dsa' ? organisation.dsaDocumentJson :
                            currentSelectedAgreement === 'dpa' ? organisation.dpaDocumentJson :
                            currentSelectedAgreement === 'toc' ? organisation.tocDocumentJson : null;

      // Set form values exactly as they are in the database
      const formData = {};
      formData[`${currentSelectedAgreement}Status`] = currentStatus;
      formData[`${currentSelectedAgreement}SignedDate`] = currentSignedDate ? moment(currentSignedDate) : null;
      formData[`${currentSelectedAgreement}SignedBy`] = currentSignedBy;
      
      // Parse existing JSON or use empty object
      let jsonData = {};
      try {
        jsonData = currentJsonStr ? JSON.parse(currentJsonStr) : {};
      } catch (e) {
        jsonData = {};
      }
      formData[currentSelectedAgreement] = jsonData;
      
      form.setFieldsValue(formData);
    }
  }, [currentSelectedAgreement, organisation, form, selectedAgreement]);

  // Notification handlers
  const [api, contextHolder] = notification.useNotification();
  const openNotification = (placement, type, description) => {
    api.info({
      message: type,
      description: description,
      placement,
      icon: type === t('Success') ? <CheckCircleTwoTone twoToneColor="#52c41a" /> : <CloseCircleTwoTone twoToneColor="#eb2f96" />,
    });
  };

  // Show success notification and update user info in localStorage
  useEffect(() => {
    if (successUpdate) {
      openNotification('bottom', t('Success'), t('Agreement updated successfully'));
      
      // Update user info in localStorage with new agreement statuses
      const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      if (userInfo.businessOrganisationId === organisation?.id) {
        const updatedUserInfo = {
          ...userInfo,
          dsaStatus: organisation.dsaStatus,
          dpaStatus: organisation.dpaStatus,
          tocStatus: organisation.tocStatus,
          dsaSignedDate: organisation.dsaSignedDate,
          dpaSignedDate: organisation.dpaSignedDate,
          tocSignedDate: organisation.tocSignedDate,
          dsaSignedBy: organisation.dsaSignedBy,
          dpaSignedBy: organisation.dpaSignedBy,
          tocSignedBy: organisation.tocSignedBy
        };
        localStorage.setItem('userInfo', JSON.stringify(updatedUserInfo));
        
        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('userInfoUpdated', { detail: updatedUserInfo }));
      }
      
      dispatch({ type: BUSINESS_ORGANISATION_UPDATE_RESET });
      // DO NOT CLOSE MODAL - keep it open as requested
    }
  }, [successUpdate, dispatch, organisation, t]);

  // Show error notification
  useEffect(() => {
    if (errorUpdate) {
      openNotification('bottom', t('Error'), t('Failed to update agreement'));
      dispatch({ type: BUSINESS_ORGANISATION_UPDATE_RESET });
    }
  }, [errorUpdate, dispatch, t]);


  const handleSign = async () => {
    try {
      if (!currentSelectedAgreement) {
        openNotification('bottom', t('Error'), t('Please select an agreement type to update'));
        return;
      }

      const values = await form.validateFields();
      
      // Get the signed by field for validation
      const signedByField = `${currentSelectedAgreement}SignedBy`;
      
      // Validate that Signed By is required when signing
      if (!values[signedByField] || values[signedByField].trim() === '') {
        openNotification('bottom', t('Error'), t('Signed By field is required when signing'));
        return;
      }

      // Check if all agreement terms are selected
      const agreementData = values[currentSelectedAgreement] || {};
      
      // Function to check if all checkboxes in a nested object are true
      const areAllChecked = (obj) => {
        return Object.values(obj).every(value => {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            return areAllChecked(value);
          }
          return value === true;
        });
      };

      if (!areAllChecked(agreementData)) {
        openNotification('bottom', t('Error'), t('All agreement terms must be checked before signing'));
        return;
      }
      
      // Prepare update data for the specific agreement only
      const updateData = {};
      updateData[`${currentSelectedAgreement}Status`] = 'Signed';
      updateData[`${currentSelectedAgreement}SignedDate`] = new Date().toISOString();
      updateData[`${currentSelectedAgreement}SignedBy`] = values[signedByField];
      updateData[`${currentSelectedAgreement}DocumentJson`] = JSON.stringify(values[currentSelectedAgreement] || {});

      dispatch(updateBusinessOrganisation(organisationId, updateData));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleSaveChanges = async () => {
    try {
      if (!currentSelectedAgreement) {
        openNotification('bottom', t('Error'), t('Please select an agreement type to update'));
        return;
      }

      const values = await form.validateFields();
      
      // Get the status and signed by field for validation
      const statusField = `${currentSelectedAgreement}Status`;
      const signedByField = `${currentSelectedAgreement}SignedBy`;
      const signedDateField = `${currentSelectedAgreement}SignedDate`;
      const jsonField = currentSelectedAgreement;
      
      const currentStatus = values[statusField];
      
      // Prepare update data for the specific agreement only
      const updateData = {};
      updateData[`${currentSelectedAgreement}Status`] = currentStatus;
      updateData[`${currentSelectedAgreement}SignedDate`] = values[signedDateField] ? values[signedDateField].toISOString() : null;
      updateData[`${currentSelectedAgreement}SignedBy`] = values[signedByField] || '';
      updateData[`${currentSelectedAgreement}DocumentJson`] = JSON.stringify(values[jsonField] || {});

      dispatch(updateBusinessOrganisation(organisationId, updateData));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };

  const handleRevoke = async () => {
    try {
      if (!currentSelectedAgreement) {
        openNotification('bottom', t('Error'), t('Please select an agreement type to update'));
        return;
      }

      const values = await form.validateFields();
      
      // Prepare update data for the specific agreement only - uncheck all boxes
      const updateData = {};
      updateData[`${currentSelectedAgreement}Status`] = 'NotSigned';
      updateData[`${currentSelectedAgreement}SignedDate`] = null;
      updateData[`${currentSelectedAgreement}SignedBy`] = '';
      
      // Uncheck all boxes by setting all values to false
      const uncheckAllBoxes = (obj, result = {}) => {
        Object.entries(obj).forEach(([key, value]) => {
          if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
            result[key] = {};
            uncheckAllBoxes(value, result[key]);
          } else {
            result[key] = false;
          }
        });
        return result;
      };
      
      // Get the default structure to uncheck all boxes
      const defaultStructure = currentSelectedAgreement === 'dsa' ? {
        dataSharing: { patientDataAccess: false, clinicalDataSharing: false, researchDataAccess: false, anonymizedDataSharing: false, dataRetentionCompliance: false, gdprCompliance: false, nhsDataStandards: false, crossOrganisationSharing: false },
        technical: { secureDataTransmission: false, encryptionStandards: false, accessControls: false, auditLogging: false, dataBackup: false, disasterRecovery: false },
        legal: { dataProtectionAct: false, nhsConstitution: false, careActCompliance: false, mentalHealthAct: false, confidentialityAgreement: false }
      } : currentSelectedAgreement === 'dpa' ? {
        processing: { lawfulBasis: false, purposeLimitation: false, dataMinimisation: false, accuracy: false, storageLimitation: false, securityMeasures: false },
        rights: { rightToInformation: false, rightOfAccess: false, rightToRectification: false, rightToErasure: false, rightToRestrictProcessing: false, rightToDataPortability: false, rightToObject: false },
        security: { technicalMeasures: false, organisationalMeasures: false, staffTraining: false, incidentReporting: false, regularReviews: false }
      } : {
        service: { serviceLevelAgreement: false, uptimeGuarantee: false, supportResponse: false, maintenanceWindows: false, performanceStandards: false },
        compliance: { nhsStandards: false, iso27001: false, cyberEssentials: false, dataSecurityProtection: false, clinicalSafety: false },
        commercial: { pricingStructure: false, paymentTerms: false, contractDuration: false, terminationClause: false, liabilityLimits: false }
      };
      
      const uncheckedJson = {};
      uncheckAllBoxes(defaultStructure, uncheckedJson);
      
      updateData[`${currentSelectedAgreement}DocumentJson`] = JSON.stringify(uncheckedJson);

      dispatch(updateBusinessOrganisation(organisationId, updateData));
    } catch (error) {
      console.error('Validation failed:', error);
    }
  };


  return (
    <>
      {contextHolder}
      <Modal
        title={
          <Space>
            {getStatusIcon()}
            <span>{t('Update Data Sharing Agreements')}</span>
          </Space>
        }
        open={visible}
        onCancel={onCancel}
        width={800}
        footer={(() => {
          const formValues = form.getFieldsValue();
          const currentFormStatus = currentSelectedAgreement ? formValues[`${currentSelectedAgreement}Status`] : null;
          const currentOrgStatus = organisation && currentSelectedAgreement ? 
            (currentSelectedAgreement === 'dsa' ? organisation.dsaStatus :
             currentSelectedAgreement === 'dpa' ? organisation.dpaStatus :
             currentSelectedAgreement === 'toc' ? organisation.tocStatus : null) : null;

          const buttons = [];

          // Show Revoke button only if agreement is signed
          if (currentOrgStatus === 'Signed') {
            buttons.push(
              <Popconfirm
                key="revoke"
                title={t('Revoke Agreement')}
                description={t('Are you sure you want to revoke this agreement?')}
                onConfirm={handleRevoke}
                okText={t('Yes, Revoke')}
                cancelText={t('Cancel')}
                okButtonProps={{ danger: true }}
              >
                 <Button 
                   type="link"
                   danger
                   icon={<CloseOutlined />}
                   loading={loadingUpdate || loadingDetails}
                 >
                  {t('Revoke')}
                </Button>
              </Popconfirm>
            );
          }


          // Show Sign button only if agreement is not signed
          if (currentOrgStatus !== 'Signed') {
            buttons.push(
              <Popconfirm
                key="sign"
                title={t('Sign Agreement')}
                description={t('Are you sure you want to sign this agreement?')}
                onConfirm={handleSign}
                okText={t('Yes, Sign')}
                cancelText={t('Cancel')}
                okButtonProps={{ type: 'primary' }}
              >
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={loadingUpdate || loadingDetails}
                >
                  {t('Accept and Sign')}
                </Button>
              </Popconfirm>
            );
          }

          return buttons;
        })()}
      >
        <Form form={form} layout="vertical">
          {/* Agreement Type Selection - only show if no specific agreement selected */}
          {!selectedAgreement && (
            <div style={{ marginBottom: 24 }}>
              <Text strong style={{ fontSize: '14px', display: 'block', marginBottom: 12 }}>
                {t('Select Agreement Type to Update')}
              </Text>
              <Radio.Group 
                value={currentSelectedAgreement} 
                onChange={(e) => setCurrentSelectedAgreement(e.target.value)}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Radio value="dsa" style={{ display: 'block', marginBottom: 8 }}>
                    <Space>
                      <CheckCircleOutlined />
                      <span>{t('Data Sharing Agreement (DSA)')}</span>
                    </Space>
                  </Radio>
                  <Radio value="dpa" style={{ display: 'block', marginBottom: 8 }}>
                    <Space>
                      <CheckCircleOutlined />
                      <span>{t('Data Processing Agreement (DPA)')}</span>
                    </Space>
                  </Radio>
                  <Radio value="toc" style={{ display: 'block', marginBottom: 8 }}>
                    <Space>
                      <CheckCircleOutlined />
                      <span>{t('Terms of Contract (TOC)')}</span>
                    </Space>
                  </Radio>
                </Space>
              </Radio.Group>
            </div>
          )}


          {currentSelectedAgreement === 'dsa' && (
            <DSAComponent 
              form={form} 
              currentStatus={form.getFieldValue('dsaStatus')}
              showTitle={true}
              organisation={organisation}
              onSaveChanges={handleSaveChanges}
              loading={loadingUpdate || loadingDetails}
            />
          )}

          {currentSelectedAgreement === 'dpa' && (
            <DPAComponent 
              form={form} 
              currentStatus={form.getFieldValue('dpaStatus')}
              showTitle={true}
              organisation={organisation}
              onSaveChanges={handleSaveChanges}
              loading={loadingUpdate || loadingDetails}
            />
          )}

          {currentSelectedAgreement === 'toc' && (
            <TOCComponent 
              form={form} 
              currentStatus={form.getFieldValue('tocStatus')}
              showTitle={true}
              organisation={organisation}
              onSaveChanges={handleSaveChanges}
              loading={loadingUpdate || loadingDetails}
            />
          )}
        </Form>
      </Modal>
    </>
  );
};

export default AgreementUpdateModal;
